// =============================================
// SERVICE: Dashboard Operacional da Transportadora
// =============================================

import { apiService } from './api';
import { buildApiUrl } from './config';

// =============================================
// INTERFACES
// =============================================

export interface DashboardFilters {
  periodo: string;
  dataInicio?: string;
  dataFim?: string;
}

export interface CargasFilters extends DashboardFilters {
  pagina?: number;
  limite?: number;
}

export interface OverviewData {
  total_paletes: number;
  total_cargas: number;
  total_etiquetas: number;
  total_divergencias: number;
  total_duplicadas: number;
  total_ocorrencias: number;
}

export interface VolumeHora {
  hora: number;
  total: number;
}

export interface AreaTotal {
  area: string;
  total: number;
}

export interface VolumeDiario {
  dia: string;
  total_leituras: number;
  ok: number;
  divergencias: number;
  duplicadas: number;
}

export interface ChartsData {
  volume_por_hora: VolumeHora[];
  etiquetas_por_area: AreaTotal[];
  pedidos_por_area: AreaTotal[];
  volume_diario: VolumeDiario[];
}

export interface ConferenteRanking {
  conferente_id: number;
  conferente_nome: string;
  total_etiquetas: number;
  total_cargas: number;
  total_divergencias: number;
  total_duplicadas: number;
  total_leituras: number;
  taxa_erro: number;
}

export interface ConferenteQualidade {
  conferente_id: number;
  conferente_nome: string;
  duplicidades: number;
  divergencias: number;
  total_leituras: number;
  taxa_erro: number;
}

export interface ConferenteProdutividade {
  conferente_id: number;
  conferente_nome: string;
  total_etiquetas: number;
  horas_trabalhadas: number;
  etiquetas_por_hora: number;
}

export interface ConferenteTempoMedio {
  conferente_id: number;
  conferente_nome: string;
  total_conferencias: number;
  tempo_medio_segundos: number;
}

export interface ConferentesData {
  ranking_produtividade: ConferenteRanking[];
  ranking_qualidade: ConferenteQualidade[];
  produtividade: ConferenteProdutividade[];
  tempo_medio: ConferenteTempoMedio[];
}

export interface PedidoArea {
  area_codigo: string;
  area_nome: string;
  area_regiao: string;
  total_pedidos: number;
  total_etiquetas: number;
  divergentes: number;
  valor_total: number;
  peso_total: number;
}

export interface CargaArea {
  area_codigo: string;
  area_nome: string;
  area_regiao: string;
  total_cargas: number;
  total_itens: number;
  total_divergencias: number;
  concluidas: number;
  tempo_medio: number;
}

export interface PaleteDia {
  dia: string;
  total_paletes: number;
  total_operacoes: number;
}

export interface RegionsData {
  pedidos_por_area: PedidoArea[];
  cargas_por_area: CargaArea[];
  paletes_por_dia: PaleteDia[];
}

export interface PaleteItem {
  id: number;
  numero_palete: number;
  numero_operacao: number;
  status: string;
  operacao_status: string;
  doca: string;
  tipo_palete: string;
  data_agendamento: string;
  area_codigo: string;
  area_nome: string;
  area_regiao: string;
  cliente_nome: string;
  conferente_nome: string;
  local_pre_carregamento: string;
  total_etiquetas: number;
  total_leituras: number;
  total_duplicadas: number;
  total_pedidos: number;
  peso_total: number;
  criado_em: string;
  atualizado_em: string;
}

export interface PaginationInfo {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

export interface PaleteEmAndamento {
  id: number;
  numero_palete: number;
  numero_operacao: number;
  status: string;
  operacao_status: string;
  doca: string;
  tipo_palete: string;
  area_codigo: string;
  area_nome: string;
  area_regiao: string;
  conferente_nome: string;
  cliente_nome: string;
  total_etiquetas: number;
  total_duplicadas: number;
  total_pedidos: number;
}

export interface ConferenteAtivo {
  conferente_id: number;
  conferente_nome: string;
  leituras_ultimos_30min: number;
  ultima_leitura: string;
}

export interface ResumoDia {
  paletes_hoje: number;
  concluidos_hoje: number;
  em_andamento_hoje: number;
  etiquetas_hoje: number;
}

export interface RealtimeData {
  paletes_em_andamento: PaleteEmAndamento[];
  conferentes_ativos: ConferenteAtivo[];
  resumo_dia: ResumoDia;
}

export interface AlertaOperacional {
  tipo: string;
  severidade: 'alta' | 'media' | 'baixa';
  titulo: string;
  descricao: string;
  dados: Record<string, unknown>;
}

// =============================================
// HELPERS
// =============================================

function buildUrlWithParams(path: string, params?: Record<string, unknown> | DashboardFilters | CargasFilters): string {
  const url = buildApiUrl(path);
  if (!params) return url;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

// =============================================
// SERVICE
// =============================================

export const operationalDashboardService = {
  getOverview: (filters: DashboardFilters = { periodo: 'hoje' }) =>
    apiService.request<{ success: boolean; data: OverviewData }>(buildUrlWithParams('/operational-dashboard/overview', filters)),

  getCharts: (filters: DashboardFilters = { periodo: 'hoje' }) =>
    apiService.request<{ success: boolean; data: ChartsData }>(buildUrlWithParams('/operational-dashboard/charts', filters)),

  getConferentes: (filters: DashboardFilters = { periodo: 'hoje' }) =>
    apiService.request<{ success: boolean; data: ConferentesData }>(buildUrlWithParams('/operational-dashboard/conferentes', filters)),

  getRegions: (filters: DashboardFilters = { periodo: 'hoje' }) =>
    apiService.request<{ success: boolean; data: RegionsData }>(buildUrlWithParams('/operational-dashboard/regions', filters)),

  getCargas: (filters: CargasFilters = { periodo: 'hoje' }) =>
    apiService.request<{ success: boolean; data: PaleteItem[]; pagination: PaginationInfo }>(buildUrlWithParams('/operational-dashboard/cargas', filters)),

  getRealtime: () =>
    apiService.request<{ success: boolean; data: RealtimeData }>(buildApiUrl('/operational-dashboard/realtime')),

  getAlerts: () =>
    apiService.request<{ success: boolean; data: AlertaOperacional[] }>(buildApiUrl('/operational-dashboard/alerts'))
};
